import { Component, type ErrorInfo, type ReactNode } from "react";

import { useT } from "@/lib/i18n";

const CHUNK_ERROR =
  /ChunkLoadError|Loading chunk \S+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/iu;

function isChunkError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === "ChunkLoadError" || CHUNK_ERROR.test(error.message);
  }
  return typeof error === "string" && CHUNK_ERROR.test(error);
}

interface State {
  failed: boolean;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: !isChunkError(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkError(error)) {
      if (globalThis.sessionStorage?.getItem("bsmeet:chunk-reload") === null) {
        globalThis.sessionStorage?.setItem("bsmeet:chunk-reload", "1");
        globalThis.location.reload();
      }
      return;
    }
    console.error("[bsmeet] render failed", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <Crashed />;
  }
}

function Crashed() {
  const t = useT();
  return (
    <div className="fixed inset-0 z-[998] flex items-center justify-center bg-stage px-6 py-12">
      <div className="w-full max-w-sm text-center">
        <h1 className="text-lg font-semibold text-stage-ink">{t("error.crashTitle")}</h1>
        <p className="mt-2 text-sm text-stage-muted">{t("error.crashBody")}</p>
        <button
          className="mt-6 flex h-11 w-full items-center justify-center rounded-xl bg-brand px-4 text-sm font-semibold text-brand-ink transition-colors hover:bg-brand-strong"
          onClick={() => {
            globalThis.location.reload();
          }}
          type="button"
        >
          {t("error.crashReload")}
        </button>
      </div>
    </div>
  );
}
